import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBooking, updateBooking } from "../services/api";
import { toast } from "react-toastify";
import { useAddBookingForm } from "./useAdminBookingForm";
import { add, format } from "date-fns";
interface UseEditBookingFormProps {
  duration?: { hours: number; minutes: number };
}

export function useEditBookingForm({
  duration = { hours: 4, minutes: 0 },
}: UseEditBookingFormProps = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [bookingData, setBookingData] = useState<any>(null);
  const [homeAddress, setHomeAddress] = useState("");
  const [landmark, setLandmark] = useState("");

  // Get the base form functionality from useAddBookingForm
  const addBookingForm = useAddBookingForm({
    onBack: () => navigate(`/control/bookings/view/${id}`),
  });

  // Extract all the state and methods from the base hook
  const {
    setGlamPackages,
    setCustomerName,
    setSelectedPackage,
    setGlamSpot,
    handleNumberOfFacesChange,
    handleCustomFacesChange,
    clientPhoto,
    setEmail,
    setPhoneNumber,
    setAdditionalInfo,
    handleDateSelect,
    handleTimeSelect,
    setClientPhotoPreview,
    setTimeSelection,
    setOriginalDate,
    setOriginalTime,
    setDateSelection,
    ...rest
  } = addBookingForm;

  // Create a custom submit handler for updating
  const handleSubmit = async () => {
    try {
      if (!id) {
        toast.error("Invalid booking ID");
        return;
      }
  
      // Validate form (keep all your existing validations)
      if (!addBookingForm.customerName) {
        toast.error("Customer name is required");
        return;
      }
  
      if (!addBookingForm.selectedPackage) {
        toast.error("Please select a glam package");
        return;
      }
  
      if (!addBookingForm.glamSpot) {
        toast.error("Please select a glam spot");
        return;
      }
  
      if (!addBookingForm.email || !addBookingForm.validateEmail(addBookingForm.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
  
      if (!addBookingForm.phoneNumber || addBookingForm.phoneNumber.length < 10) {
        toast.error("Please enter a valid phone number");
        return;
      }
  
      if (!addBookingForm.dateSelection.selectedDate) {
        toast.error("Please select a date");
        return;
      }
  
      if (!addBookingForm.timeSelection.selectedTime24h) {
        toast.error("Please select a time");
        return;
      }

      // Add validation for home address if home service is selected
      if (addBookingForm.glamSpot === "home" && !homeAddress.trim()) {
        toast.error("Home address is required for home service");
        return;
      }
  
      // Calculate number of faces (use custom count if "more" is selected)
      const facesCount = addBookingForm.numberOfFaces === "more"
        ? parseInt(addBookingForm.customFacesCount || "1", 10)
        : parseInt(addBookingForm.numberOfFaces || "1", 10);
      
      // Ensure facesCount is at least 1
      const adjustedFacesCount = Math.max(1, facesCount);
  
      // Calculate adjusted duration based on face count
      const adjustedDuration = {
        hours: duration.hours * adjustedFacesCount,
        minutes: duration.minutes * adjustedFacesCount,
      };
  
      // Create datetime objects with adjusted duration
      const startDateTime = new Date(
        `${addBookingForm.dateSelection.selectedDate} ${addBookingForm.timeSelection.selectedTime24h}:00`
      );
      const endDateTime = add(startDateTime, {
        hours: adjustedDuration.hours,
        minutes: adjustedDuration.minutes,
      });
  
      // Format for API submission
      const bookingStartDateTime = format(startDateTime, "yyyy-MM-dd HH:mm:ss");
      const bookingEndDateTime = format(endDateTime, "yyyy-MM-dd HH:mm:ss");
  
      // Map glamSpot value to match API expectations
      const glamSpotValue = addBookingForm.glamSpot === "studio"
        ? "studio_session"
        : "home_service";
  
      // Create FormData for the update
      const formData = new FormData() as any;
      formData.append("name", addBookingForm.customerName);
      formData.append("email_address", addBookingForm.email);
      formData.append("phone_number", addBookingForm.phoneNumber);
      formData.append("glam_package_id", addBookingForm.selectedPackage);
      formData.append("glam_spot", glamSpotValue);
      if (addBookingForm.glamSpot === "home") {
        if (!homeAddress.trim()) {
          toast.error("Home address is required for home service");
          return;
        }
        formData.append("home_address", homeAddress);
        if (landmark.trim()) {
          formData.append("landmark", landmark);
        }
      }
      formData.append("number_of_faces", adjustedFacesCount.toString());
      formData.append("booking_start_date_time", bookingStartDateTime);
      formData.append("booking_end_date_time", bookingEndDateTime);
      
      // Only append additional_info if it exists
      if (addBookingForm.additionalInfo && addBookingForm.additionalInfo.trim() !== "") {
        formData.append("additional_info", addBookingForm.additionalInfo);
      }
      
      // Append client photo if a new one was uploaded
      if (addBookingForm.clientPhoto) {
        formData.append("client_photo", addBookingForm.clientPhoto);
      } else if (bookingData.media_url) {
        // Keep existing media_url if no new photo was uploaded
        formData.append("media_url", bookingData.media_url);
      }
      
      formData.append("featured_image_discount", bookingData.featured_image_discount.toString());
      formData.append("status", bookingData.status);
  
      await updateBooking(id, formData);
      toast.success("Booking updated successfully!");
      navigate(`/control/bookings/view/${id}`);
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking");
    }
  };

  // Fetch booking data on component mount
  useEffect(() => {
    // In your fetchBookingData useEffect:
    const fetchBookingData = async () => {

      if (!id) {
        toast.error("Invalid booking ID");
        navigate("/control/bookings");
        return;
      }

      try {
        setIsLoading(true);
        const response = (await getBooking(id)) as any;
        const booking = response.data.data;
        setBookingData(booking);

        // Set original values
        const bookingDateTime = new Date(booking.booking_start_date_time);
        const dateString = format(bookingDateTime, "yyyy-MM-dd");
        const timeString = format(bookingDateTime, "HH:mm");

        // Set the original values in the form
        setOriginalDate(dateString);
        setOriginalTime(timeString);

        // Prefill form
        setCustomerName(booking.name);
        setSelectedPackage(booking.glam_package_id);
        setGlamSpot(booking.glam_spot === "studio_session" ? "studio" : "home");
        if (booking.glam_spot === "home_service") {
          setHomeAddress(booking.home_address || "");
          setLandmark(booking.landmark || "");
        }

        // Handle number of faces
        if (Number.parseInt(booking.number_of_faces, 10) <= 5) {
          handleNumberOfFacesChange(booking.number_of_faces.toString());
        } else {
          handleNumberOfFacesChange("more");
          handleCustomFacesChange(booking.number_of_faces.toString());
        }

        // Set date and time without triggering availability checks
        setDateSelection({
          selectedDate: dateString,
          isAvailable: true,
          isLoading: false,
        });

        setTimeSelection({
          selectedTime24h: timeString,
          selectedTimeDisplay: format(bookingDateTime, "h:mm a"),
          isAvailable: true,
          isLoading: false,
        });

        // Set other fields
        setEmail(booking.email_address);
        setPhoneNumber(booking.phone_number);
        setAdditionalInfo(booking.additional_info || "");

        if (booking.media_url) {
          setClientPhotoPreview(booking.media_url);
        }
      } catch (error) {
        // Error handling
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingData();
  }, [id, navigate]);

  return {
    ...addBookingForm,
    isLoading,
    bookingData,
    handleSubmit,
    homeAddress,
    setHomeAddress,
    landmark,
    setLandmark,
  };
}
